import { Alert, Button, DialogActions, DialogContent, LinearProgress, Skeleton, styled, Typography } from 'opensolar-ui'

import { BULK_EDIT_FORM_ID, bulkEditDialogActionsSx } from './constants'

type Translate = (key: string, options?: Record<string, unknown>) => string

const BulkEditResolvingTitle = styled(Typography)(({ theme }) => ({
  marginTop: 20,
  display: 'block',
}))

const BulkEditResolvingSkeletonStack = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  width: '100%',
}))

const BulkEditAutoAcceptPendingRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3, 0),
  minHeight: 160,
  width: '100%',
}))

const BulkEditAutoApplyLinearProgress = styled(LinearProgress)({
  width: '100%',
  alignSelf: 'stretch',
})

export function BulkEditNoPermissionState({ translate }: { translate: Translate }) {
  return (
    <DialogContent margin="normal">
      <Typography>{translate('You do not have permission to edit these records.')}</Typography>
    </DialogContent>
  )
}

export function BulkEditResolvingState({ translate }: { translate: Translate }) {
  return (
    <DialogContent margin="normal">
      <BulkEditResolvingTitle textVariant="body1SemiBold">
        {translate('Fetching records to update.')}
      </BulkEditResolvingTitle>
      <BulkEditResolvingSkeletonStack>
        <Skeleton variant="rounded" height={20} width="52%" />
        <Skeleton variant="rounded" height={48} width="100%" />
      </BulkEditResolvingSkeletonStack>
    </DialogContent>
  )
}

export function BulkEditResolveFailedState({ message }: { message: string }) {
  return (
    <DialogContent margin="normal">
      <Alert severity="error">{message}</Alert>
    </DialogContent>
  )
}

export function BulkEditEmptySelectionState({ translate }: { translate: Translate }) {
  return (
    <DialogContent margin="normal">
      <Alert severity="info" style={{ marginTop: 20 }}>
        {translate("You're all up to date")}
      </Alert>
    </DialogContent>
  )
}

export function BulkEditAutoAcceptWarningState({ message }: { message: string }) {
  return (
    <DialogContent margin="normal">
      <Alert severity="warning">{message}</Alert>
    </DialogContent>
  )
}

export function BulkEditBulkSaveFailedIdBlock({
  translate,
  effectiveCount,
  effectiveIds,
}: {
  translate: Translate
  effectiveCount: number
  effectiveIds: readonly (string | number)[]
}) {
  const BULK_EDIT_ERROR_ID_SAMPLE_MAX = 25
  const shown = effectiveIds.slice(0, BULK_EDIT_ERROR_ID_SAMPLE_MAX)
  const moreCount = Math.max(0, effectiveCount - shown.length)
  if (shown.length === 0) return null
  return (
    <>
      <Typography variant="body2" color="textSecondary" style={{ display: 'block', marginTop: 16 }}>
        {translate('Record IDs in this selection:')}
      </Typography>
      <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        {shown.map((id) => (
          <li key={String(id)} style={{ marginTop: 4 }}>
            <Typography variant="body2" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {String(id)}
            </Typography>
          </li>
        ))}
      </ul>
      {moreCount > 0 ? (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 12 }}>
          {translate('And %{count} more.', { count: moreCount })}
        </Typography>
      ) : null}
    </>
  )
}

export function BulkEditAutoAcceptErrorState({
  translate,
  effectiveCount,
  effectiveIds,
}: {
  translate: Translate
  effectiveCount: number
  effectiveIds: readonly (string | number)[]
}) {
  return (
    <DialogContent margin="normal">
      <Alert severity="error">
        <Typography variant="body1SemiBold" style={{ display: 'block' }}>
          {translate('Something went wrong')}
        </Typography>
        <Typography variant="body2" style={{ display: 'block', marginTop: 10 }}>
          {translate("We couldn't save your changes.")}
        </Typography>
      </Alert>
      <BulkEditBulkSaveFailedIdBlock
        translate={translate}
        effectiveCount={effectiveCount}
        effectiveIds={effectiveIds}
      />
    </DialogContent>
  )
}

export function BulkEditBulkConfirmPanel({
  message,
  selectedCountText,
  onBack,
  onApply,
  applying,
  translate,
  backButtonKey = 'ra.action.back',
  applyButtonKey = 'ra.action.save',
  saveFailed,
  failedRecordIds,
  failedRecordCount,
}: {
  message: string
  selectedCountText: string
  onBack: () => void
  onApply: () => void | Promise<void>
  applying: boolean
  translate: Translate
  backButtonKey?: string
  applyButtonKey?: string
  saveFailed?: boolean
  failedRecordIds?: readonly (string | number)[]
  failedRecordCount?: number
}) {
  return (
    <>
      <DialogContent margin="normal" style={{ marginTop: 20 }}>
        {saveFailed && failedRecordIds && failedRecordCount != null ? (
          <>
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <Typography variant="body1SemiBold" style={{ display: 'block' }}>
                {translate('Something went wrong')}
              </Typography>
              <Typography variant="body2" style={{ display: 'block', marginTop: 10 }}>
                {translate("We couldn't save your changes.")}
              </Typography>
            </Alert>
            <BulkEditBulkSaveFailedIdBlock
              translate={translate}
              effectiveCount={failedRecordCount}
              effectiveIds={failedRecordIds}
            />
            <Typography variant="body2" style={{ display: 'block', marginTop: 20, opacity: 0.85 }}>
              {selectedCountText}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body1" style={{ display: 'block', marginBottom: 12 }}>
              {message}
            </Typography>
            <Typography variant="body2" style={{ display: 'block', opacity: 0.85 }}>
              {selectedCountText}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={bulkEditDialogActionsSx}>
        <Button variant="outlined" onClick={onBack} disabled={applying}>
          {translate(backButtonKey)}
        </Button>
        <Button variant="contained" color="primary" onClick={() => void onApply()} disabled={applying}>
          {translate(applyButtonKey)}
        </Button>
      </DialogActions>
    </>
  )
}

export function BulkEditAutoAcceptPendingState({
  translate,
  effectiveCount,
}: {
  translate: Translate
  effectiveCount: number
}) {
  return (
    <DialogContent margin="normal">
      <BulkEditAutoAcceptPendingRoot>
        <BulkEditAutoApplyLinearProgress variant="indeterminate" color="secondary" />
        <Typography variant="body1" style={{ display: 'block', textAlign: 'center' }}>
          {translate('Applying bulk changes')}
        </Typography>
        <Typography variant="body2" style={{ display: 'block', opacity: 0.85, textAlign: 'center' }}>
          {translate('Selected %{smart_count} record |||| Selected %{smart_count} records', {
            smart_count: effectiveCount,
          })}
        </Typography>
      </BulkEditAutoAcceptPendingRoot>
    </DialogContent>
  )
}

type FormActionsProps = {
  onClose: () => void
  translate: Translate
  applying: boolean
  submitting: boolean
}

export function BulkEditFormActions({ onClose, translate, applying, submitting }: FormActionsProps) {
  return (
    <DialogActions sx={bulkEditDialogActionsSx}>
      <Button variant="outlined" onClick={onClose} disabled={applying || submitting}>
        {translate('ra.action.cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        type="submit"
        form={BULK_EDIT_FORM_ID}
        disabled={applying || submitting}
      >
        {translate('ra.action.save')}
      </Button>
    </DialogActions>
  )
}
